'use client'

/* ============================================================
   RR Design System — ThemeToggle
   Drop-in icon button that flips between light & dark using
   the rr-ui ThemeProvider. Apps can compose richer pickers
   themselves via useTheme() if they want a tri-state
   (light / dark / system) selector.
   ============================================================ */

import { Moon, Sun } from 'lucide-react'
import { useTheme } from './theme-provider'
import { cn } from '../utils'

export interface ThemeToggleProps {
  className?: string
  /** Accessible label. Defaults to a sensible English string. */
  label?: string
}

export function ThemeToggle({
  className,
  label = 'Toggle color theme',
}: ThemeToggleProps) {
  const { resolvedTheme, toggleTheme } = useTheme()
  const isDark = resolvedTheme === 'dark'

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={toggleTheme}
      className={cn(
        'inline-flex h-8 w-8 items-center justify-center rounded-md',
        'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'transition-colors',
        className,
      )}
    >
      {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  )
}
